import React, { useEffect, useRef, useState } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import AgreementForm from '../components/AgreementForm';
import { LoadScript, Autocomplete } from '@react-google-maps/api';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

const libraries = ['places'];
const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export default function RentCarPage() {
  const [availableCars, setAvailableCars] = useState([]);
  const [formData, setFormData] = useState({
    carId: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    startDate: '',
    startTime: '',
    endDate: '', // new
    weeklyRate: '', // new
    securityDeposit: '', // new
    licenseFrontImage: null,
    licenseBackImage: null,
    licenseNumber: '',
    licenseState: '',
  });

  const [rentalData, setRentalData] = useState(null);
  const [step, setStep] = useState(1);
  const [ownerEmail, setOwnerEmail] = useState('pulinduh@gmail.com'); // Default fallback
  const navigate = useNavigate();
  const autoCompleteRef = useRef(null);

  useEffect(() => {
    const fetchAvailableCars = async () => {
      const snapshot = await getDocs(collection(db, 'cars'));
      const cars = snapshot.docs
        .filter(doc => doc.data().status === 'available')
        .map(doc => ({ id: doc.id, ...doc.data() }));
      setAvailableCars(cars);
    };
    fetchAvailableCars();
  }, []);

  useEffect(() => {
    const fetchOwnerEmail = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'owner'));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          if (data.email) {
            setOwnerEmail(data.email);
          }
        }
      } catch (error) {
        console.error('Error fetching owner email:', error);
        // Keep the default fallback email
      }
    };
    fetchOwnerEmail();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'licenseFrontImage' || name === 'licenseBackImage') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handlePlaceChanged = () => {
    const place = autoCompleteRef.current.getPlace();
    if (place?.formatted_address) {
      setFormData(prev => ({ ...prev, customerAddress: place.formatted_address }));
    }
  };

  const proceedToStep2 = (e) => {
    e.preventDefault();
    // Validate step 1 fields
    if (!formData.carId || !formData.customerName || !formData.customerEmail || !formData.customerPhone || !formData.customerAddress) {
      alert('Please fill in all required fields.');
      return;
    }
    setStep(2);
  };

  const proceedToStep3 = (e) => {
    e.preventDefault();
    // Validate step 2 fields
    if (!formData.startDate || !formData.startTime || !formData.endDate || !formData.weeklyRate || !formData.securityDeposit || !formData.licenseNumber || !formData.licenseState || !formData.licenseFrontImage || !formData.licenseBackImage) {
      alert('Please fill in all required fields.');
      return;
    }
    setStep(3);
  };

  useEffect(() => {
    if (step === 3) {
      const selectedCar = availableCars.find(car => car.id === formData.carId);

      // Upload license images to Firebase Storage
      let licenseFrontImageURL = '';
      let licenseBackImageURL = '';
      if (formData.licenseFrontImage) {
        const frontRef = ref(storage, `licenses/${Date.now()}-front-${formData.licenseFrontImage.name}`);
        uploadBytes(frontRef, formData.licenseFrontImage).then(() => getDownloadURL(frontRef))
          .then(url => {
            licenseFrontImageURL = url;
            if (formData.licenseBackImage) {
              const backRef = ref(storage, `licenses/${Date.now()}-back-${formData.licenseBackImage.name}`);
              return uploadBytes(backRef, formData.licenseBackImage).then(() => getDownloadURL(backRef));
            }
            return null;
          })
          .then(url => {
            if (url) licenseBackImageURL = url;
            
            const { licenseFrontImage, licenseBackImage, startDate, startTime, customerEmail, customerName, ...rest } = formData;
            const dateTimeString = `${startDate}T${startTime}`;
            const startDateTime = new Date(dateTimeString);

            setRentalData({
              ...rest,
              customerEmail,
              customerName,
              startDate: startDateTime.toISOString(),
              endDate: formData.endDate,
              weeklyRate: formData.weeklyRate,
              securityDeposit: formData.securityDeposit,
              carMake: selectedCar.make,
              carModel: selectedCar.model,
              carRego: selectedCar.rego,
              carColor: selectedCar.color,
              carYear: selectedCar.year,
              ownerEmail: ownerEmail,
              licenseFrontImageURL,
              licenseBackImageURL,
            });
          });
      } else if (formData.licenseBackImage) {
        const backRef = ref(storage, `licenses/${Date.now()}-back-${formData.licenseBackImage.name}`);
        uploadBytes(backRef, formData.licenseBackImage).then(() => getDownloadURL(backRef))
          .then(url => {
            licenseBackImageURL = url;
            
            const { licenseFrontImage, licenseBackImage, startDate, startTime, customerEmail, customerName, ...rest } = formData;
            const dateTimeString = `${startDate}T${startTime}`;
            const startDateTime = new Date(dateTimeString);

            setRentalData({
              ...rest,
              customerEmail,
              customerName,
              startDate: startDateTime.toISOString(),
              endDate: formData.endDate,
              weeklyRate: formData.weeklyRate,
              securityDeposit: formData.securityDeposit,
              carMake: selectedCar.make,
              carModel: selectedCar.model,
              carRego: selectedCar.rego,
              carColor: selectedCar.color,
              carYear: selectedCar.year,
              ownerEmail: ownerEmail,
              licenseFrontImageURL,
              licenseBackImageURL,
            });
          });
      } else {
        const { licenseFrontImage, licenseBackImage, startDate, startTime, customerEmail, customerName, ...rest } = formData;
        const dateTimeString = `${startDate}T${startTime}`;
        const startDateTime = new Date(dateTimeString);

        setRentalData({
          ...rest,
          customerEmail,
          customerName,
          startDate: startDateTime.toISOString(),
          endDate: formData.endDate,
          weeklyRate: formData.weeklyRate,
          securityDeposit: formData.securityDeposit,
          carMake: selectedCar.make,
          carModel: selectedCar.model,
          carRego: selectedCar.rego,
          carColor: selectedCar.color,
          carYear: selectedCar.year,
          ownerEmail: ownerEmail,
          licenseFrontImageURL,
          licenseBackImageURL,
        });
      }
    }
  }, [step, formData, availableCars, ownerEmail]);

  return (
    <LoadScript googleMapsApiKey={googleMapsApiKey} libraries={libraries}>
      <div style={pageWrapper}>
        <div style={formCard}>
          <div style={formHeader}>
            <h2 style={formTitle}>📄 Rent a Car</h2>
            <button onClick={() => navigate('/')} style={backButton}>← Back</button>
          </div>

          {/* Step Progress Indicator */}
          <div style={progressContainer}>
            <div style={progressStep(step >= 1)}>
              <div style={progressNumber}>1</div>
              <span>Car & Customer</span>
            </div>
            <div style={progressLine}></div>
            <div style={progressStep(step >= 2)}>
              <div style={progressNumber}>2</div>
              <span>Rental Details</span>
            </div>
            <div style={progressLine}></div>
            <div style={progressStep(step >= 3)}>
              <div style={progressNumber}>3</div>
              <span>Agreement</span>
            </div>
          </div>

          {/* Step 1: Car Selection & Customer Details */}
          {step === 1 && (
            <form onSubmit={proceedToStep2}>
              <h3 style={stepTitle}>Step 1: Car Selection & Customer Details</h3>
              
              <div style={fieldGroup}>
                <label style={label}>Select a Car</label>
                <select
                  name="carId"
                  value={formData.carId}
                  onChange={handleChange}
                  required
                  style={{ ...input, cursor: 'pointer' }}
                >
                  <option value="">-- Select Available Car --</option>
                  {availableCars.map(car => (
                    <option key={car.id} value={car.id}>
                      {car.make} {car.model} ({car.rego})
                    </option>
                  ))}
                </select>
              </div>

              <div style={fieldGroup}>
                <label style={label}>Customer Name</label>
                <input name="customerName" value={formData.customerName} onChange={handleChange} required style={input} />
              </div>

              <div style={fieldGroup}>
                <label style={label}>Customer Email</label>
                <input type="email" name="customerEmail" value={formData.customerEmail} onChange={handleChange} required style={input} />
              </div>

              <div style={fieldGroup}>
                <label style={label}>Phone Number</label>
                <input type="tel" name="customerPhone" value={formData.customerPhone} onChange={handleChange} required style={input} />
              </div>

              <div style={fieldGroup}>
                <label style={label}>Address</label>
                <Autocomplete onLoad={ref => autoCompleteRef.current = ref} onPlaceChanged={handlePlaceChanged}>
                  <input name="customerAddress" value={formData.customerAddress} onChange={handleChange} required style={input} />
                </Autocomplete>
              </div>

              <button type="submit" style={submitButton}>Next: Rental Details</button>
            </form>
          )}

          {/* Step 2: Rental Details & License */}
          {step === 2 && (
            <form onSubmit={proceedToStep3}>
              <h3 style={stepTitle}>Step 2: Rental Details & License</h3>
              
              <div style={fieldGroup}>
                <label style={label}>Start Date</label>
                <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} required style={input} />
              </div>

              <div style={fieldGroup}>
                <label style={label}>Start Time</label>
                <input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  required
                  style={input}
                />
              </div>

              <div style={fieldGroup}>
                <label style={label}>Rental End Date</label>
                <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} required style={input} />
              </div>

              <div style={fieldGroup}>
                <label style={label}>Weekly Rate ($)</label>
                <input type="number" name="weeklyRate" value={formData.weeklyRate} onChange={handleChange} required style={input} min="0" step="0.01" />
              </div>

              <div style={fieldGroup}>
                <label style={label}>Security Deposit ($)</label>
                <input type="number" name="securityDeposit" value={formData.securityDeposit} onChange={handleChange} required style={input} min="0" step="0.01" />
              </div>

              <div style={fieldGroup}>
                <label style={label}>Driver's License Number</label>
                <input
                  name="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleChange}
                  required
                  style={input}
                />
              </div>

              <div style={fieldGroup}>
                <label style={label}>State of Issue</label>
                <input
                  name="licenseState"
                  value={formData.licenseState}
                  onChange={handleChange}
                  required
                  style={input}
                />
              </div>

              <div style={fieldGroup}>
                <label style={label}>License Image (Front)</label>
                <input
                  type="file"
                  name="licenseFrontImage"
                  accept="image/*"
                  onChange={handleChange}
                  required
                  style={{ ...input, padding: '10px' }}
                />
              </div>

              <div style={fieldGroup}>
                <label style={label}>License Image (Back)</label>
                <input
                  type="file"
                  name="licenseBackImage"
                  accept="image/*"
                  onChange={handleChange}
                  required
                  style={{ ...input, padding: '10px' }}
                />
              </div>

              <div style={buttonContainer}>
                <button type="button" onClick={() => setStep(1)} style={backStepButton}>Back</button>
                <button type="submit" style={submitButton}>Next: Sign Agreement</button>
              </div>
            </form>
          )}

          {/* Step 3: Agreement Signing */}
          {step === 3 && rentalData && (
            <div>
              <h3 style={stepTitle}>Step 3: Sign Agreement</h3>
              <AgreementForm
                rentalData={rentalData}
                availableCars={availableCars}
                onComplete={() => {
                  alert('✅ Agreement signed and rental recorded!');
                  navigate('/');
                }}
              />
            </div>
          )}
        </div>
      </div>
    </LoadScript>
  );
}

// Inline Styles
const pageWrapper = { backgroundColor: '#f4f6f8', minHeight: '100vh', display: 'flex', justifyContent: 'center', padding: '40px 20px' };
const formCard = { backgroundColor: '#fff', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '600px', boxShadow: '0 8px 30px rgba(0,0,0,0.06)' };
const formHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' };
const formTitle = { fontSize: '22px', fontWeight: 600, color: '#111827' };
const backButton = { padding: '6px 12px', fontSize: '14px', border: 'none', borderRadius: '8px', backgroundColor: '#e5e7eb', color: '#374151', fontWeight: 500, cursor: 'pointer' };
const fieldGroup = { marginBottom: '18px' };
const label = { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' };
const input = { width: '100%', padding: '12px 14px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '15px', backgroundColor: '#f9fafb', outline: 'none', boxSizing: 'border-box' };
const submitButton = { width: '100%', padding: '14px', background: 'linear-gradient(to right, #16a34a, #22c55e)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 600, cursor: 'pointer' };
const stepTitle = { fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '20px' };
const buttonContainer = { display: 'flex', gap: '12px', marginTop: '20px' };
const backStepButton = { flex: 1, padding: '14px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 600, cursor: 'pointer' };
const progressContainer = { display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '30px', gap: '8px' };
const progressStep = (active) => ({ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', opacity: active ? 1 : 0.5, transition: 'opacity 0.3s' });
const progressNumber = { width: '32px', height: '32px', borderRadius: '50%', background: '#16a34a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 600 };
const progressLine = { width: '40px', height: '2px', background: '#e5e7eb' };
